import { Fragment } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { List as MuiList, ListItem as MuiListItem } from "@mui/material";

// Thin wrapper presenting antd's List API (dataSource/renderItem + List.Item)
// on MUI List/ListItem. Covers the subset used in shared/frontend: simple
// vertical lists with a render callback; List.Item forwards DOM/aria props.
// Not covered (add when a call site needs it): grid, pagination, List.Item.Meta,
// actions/extra, itemLayout="horizontal".

export interface ListProps<T> {
  dataSource?: T[];
  renderItem?: (item: T, index: number) => ReactNode;
  size?: "small" | "default" | "large";
  header?: ReactNode;
  footer?: ReactNode;
  loading?: boolean;
  locale?: { emptyText?: ReactNode };
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

export interface ListItemProps {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: (e: MouseEvent<HTMLLIElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLLIElement>) => void;
  // antd List.Item spreads arbitrary DOM/aria/data-* props onto the <li>.
  [key: string]: unknown;
}

const ListItem = ({ children, style, className, ...rest }: ListItemProps) => (
  <MuiListItem disableGutters disablePadding style={style} className={className} {...rest}>
    {children}
  </MuiListItem>
);

export function List<T = unknown>({
  dataSource,
  renderItem,
  size,
  header,
  footer,
  loading,
  locale,
  style,
  className,
  children,
}: ListProps<T>) {
  const isEmpty = !!dataSource && dataSource.length === 0;
  const body =
    dataSource && renderItem
      ? dataSource.map((item, i) => <Fragment key={i}>{renderItem(item, i)}</Fragment>)
      : children;
  return (
    <MuiList dense={size === "small"} style={style} className={className}>
      {header}
      {loading ? null : isEmpty ? locale?.emptyText ?? null : body}
      {footer}
    </MuiList>
  );
}

List.Item = ListItem;

export default List;
